import { Box, Grid, Tooltip, Typography, makeStyles } from '@material-ui/core';
import SGSCalendar from 'components/SGSCalendar';
import { useCalendarContext } from 'components/SGSCalendar/CalendarContext ';
import moment from 'moment';
import { useEffect } from 'react';

type Props = {
    events: any;
};

const IndemniteCalendar: React.FC<Props> = ({ events }) => {
    const { selectedEvent, setEvent } = useCalendarContext();

    useEffect(() => {
        // Find the last event
        const reverseOrder = [...events];
        reverseOrder.sort((a, b) => b.end - a.end);
        setEvent(reverseOrder[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const CustomEvent = ({ event, ...restProps }) => {
        const classes = useStyles();

        return (
            <>
                <Tooltip placement="top" arrow title={CustomTooltipContent(event)} classes={{ tooltip: classes.customTooltip, arrow: classes.customArrow }} TransitionProps={{ timeout: 0 }}>
                    <Box
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        style={{
                            height: '19px',
                            background: parseFloat(event.montantNet) < 0 ? '#EB4C37' : '#37adfd',
                            paddingLeft: '0',
                            paddingRight: '0',
                        }}
                        onClick={() => setEvent(event)}
                    >
                        <Typography style={{ fontSize: '.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1' }} title={''}>
                            {!restProps.continuesPrior && (
                                <>
                                    <Box display={'flex'} style={{ marginLeft: '-2px' }}>
                                        <Box style={{ width: '12px', background: '#2574a9' }}> </Box>
                                        <Box className="ml-1">&nbsp;{event.title}</Box>
                                    </Box>
                                </>
                            )}
                        </Typography>
                        <Typography style={{ fontSize: '.8rem', overflow: 'hidden' }}>
                            {!restProps.continuesAfter && (
                                <>
                                    <Box display={'flex'}>
                                        <Box
                                            className="mr-1 text-nowrap"
                                            style={{
                                                minWidth: '10px',
                                                color: parseFloat(event.montantNet) < 0 ? '#FFF' : 'inherit',
                                            }}
                                        >
                                            {formattedNumber(event.montantNet)} €
                                        </Box>
                                        <Box style={{ width: '10px', background: '#2574a9' }}></Box>
                                        <Box style={{ width: '0px' }}>
                                            {parseFloat(event.montantNet) < 0 ? (
                                                <Box style={{ position: 'absolute', color: '#2574A9', marginTop: '-20px', marginLeft: '-147px' }}>
                                                    {'Trop-perçu du '}
                                                    {moment(event.dateVirement).format('DD/MM/YYYY')}{' '}
                                                </Box>
                                            ) : (
                                                <Box style={{ position: 'absolute', color: '#2574A9', marginTop: '-20px', marginLeft: '-107px' }}>
                                                    Payé le {moment(event.dateVirement).format('DD/MM/YYYY')}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </Typography>
                    </Box>
                </Tooltip>
            </>
        );
    };

    function countEventsWithinPeriods() {
        let count = 0;
        for (let i = 0; i < events.length; i++) {
            for (let j = i + 1; j < events.length; j++) {
                const event1 = events[i];
                const event2 = events[j];
                // Convert start and end dates to Date objects for comparison
                const startDate1 = new Date(event1.start);
                const endDate1 = new Date(event1.end);
                const startDate2 = new Date(event2.start);
                const endDate2 = new Date(event2.end);
                // Check if there is an overlap in periods
                if (
                    (startDate1 <= endDate2 && startDate2 <= endDate1) || // Check for overlap
                    (startDate2 <= endDate1 && startDate1 <= endDate2)
                ) {
                    // Events overlap, increment count
                    count++;
                }
            }
        }
        // Return the count of overlapping events
        return count;
    }

    const calcTableHeight = () => {
        const count = countEventsWithinPeriods();

        const defaultHeight = 450;
        const calendarHeader = 64;
        const dayLineHeight = 20 * 6;
        const eventHeight = 40;
        const lineMargin = 10;
        const height = count > 1 ? dayLineHeight + eventHeight * count * 6 + calendarHeader + lineMargin * count : defaultHeight;
        return height + 'px';
    };

    return selectedEvent ? (
        <Grid container spacing={2} className="mt-3">
            <Grid item xs={12} md={1}></Grid>
            <Grid item xs={12} md={10}>
                <Box style={{ height: calcTableHeight() }}>
                    <SGSCalendar events={events} CustomEvent={CustomEvent} />
                </Box>
            </Grid>
            <Grid item xs={12} md={1}></Grid>
        </Grid>
    ) : (
        <Box style={{ height: '426px' }}></Box>
    );
};

export default IndemniteCalendar;

const useStyles = makeStyles(() => ({
    customTooltip: {
        backgroundColor: '#008AD5',
        color: 'white',
        maxWidth: '400px',
        fontSize: '1rem',
        padding: '5px',
        borderRadius: '5px',
    },
    customArrow: {
        position: 'absolute',
        width: 0,
        height: 0,
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderBottom: '8px solid #008AD5',
        top: '98%',
        left: '50%',
        transform: 'translateX(-50%) rotate(180deg)',
    },
    startDate: {
        color: '#4f7793',
        fontSize: '0.7rem',
    },
    endDate: {
        color: '#4f7793',
        fontSize: '0.7rem',
        width: '70px',
        background: 'red',
    },
}));

const CustomTooltipContent = (event) => {
    return (
        <>
            <Box
                style={{
                    background: '#fff',
                    borderRadius: '5px',
                    color: '#3d454c',
                    padding: '10px',
                    fontFamily: 'Open Sans',
                    fontSize: '0.8rem',
                    lineHeight: '1rem',
                    fontStyle: 'normal',
                    fontWeight: 400,
                }}
            >
                <Box className="text-center">
                    Du {moment(event.start, 'YYYY-MM-DD').format('DD/MM/YYYY')} au {moment(event.end, 'YYYY-MM-DD').format('DD/MM/YYYY')}
                </Box>
                <Box className="text-center">
                    {event.title} : {formattedNumber(event.montantNet)} €
                </Box>
            </Box>
        </>
    );
};

const formattedNumber = (number) => {
    const numberAsFloat = parseFloat(number);
    if (!isNaN(numberAsFloat)) {
        return numberAsFloat.toLocaleString('fr-FR', {
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    } else {
        return 'Invalid input';
    }
};
